import type { ButtonHTMLAttributes } from "react";
import { Link, type LinkProps } from "react-router";
import { cx } from "./cx";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "md" | "sm";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-accent-strong text-white shadow-hard hover:bg-accent-deep hover:text-white hover:shadow-hard-lg",
  secondary: "bg-card shadow-hard-sm hover:shadow-hard",
  danger: "bg-bad shadow-hard-sm hover:bg-[#f6aca7] hover:shadow-hard",
  ghost: "border-transparent bg-transparent shadow-none hover:border-ink hover:bg-card",
};

const SIZE: Record<ButtonSize, string> = {
  md: "min-h-11 px-5 py-2.5 font-semibold",
  sm: "min-h-10 px-4 py-1.5 font-medium",
};

/**
 * Neobrutalist pill button: hard border and shadow. It lifts a little on hover and presses flat when clicked.
 * Shared by <Button> and <ButtonLink>.
 */
export function buttonClass(variant: ButtonVariant = "secondary", size: ButtonSize = "md", extra?: string) {
  return cx(
    "inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink text-ink no-underline cursor-pointer select-none",
    "motion-safe:transition-[transform,box-shadow,background-color] motion-safe:duration-300 motion-safe:ease-out-soft",
    "hover:text-ink hover:-translate-x-px hover:-translate-y-px",
    "active:translate-x-[2px] active:translate-y-[2px] active:shadow-none active:duration-75",
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:active:translate-x-0 disabled:active:translate-y-0",
    VARIANT[variant],
    SIZE[size],
    extra,
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button and shows a working label while a request runs. */
  pending?: boolean;
  pendingLabel?: string;
}

export function Button({ variant, size, pending, pendingLabel, className, disabled, children, type = "button", ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} disabled={disabled || pending} aria-busy={pending || undefined} {...rest}>
      {pending ? (pendingLabel ?? "working…") : children}
    </button>
  );
}

export function ButtonLink({ variant, size, className, ...rest }: LinkProps & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}
